import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TdDialogService } from '@covalent/core/dialogs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TickService } from 'src/app/shared/tick.service';
import { countdownInSeconds } from 'src/util/rxjs/observables';
import { firstValueOrUndefined } from 'src/util/rxjs/observables';
import { AppInsightsService } from 'src/lib/tcs-app-insights/app-insights-initializer.service';
import { NewRelicService } from 'src/lib/tcs-new-relic/new-relic-initializer.service';

@Injectable({ providedIn: 'root' })
export class AppUpdaterService implements OnDestroy {
  private _onDestroy$ = new Subject();

  constructor(
    private readonly _updates: SwUpdate,
    private readonly _tick: TickService,
    private _dialogService: TdDialogService,
    private _appInsightsService: AppInsightsService,
    private _newRelicService: NewRelicService,
  ) {}

  private pollForUpdates() {
    this._tick.everyMinute$.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      this._updates.checkForUpdate().catch((reason) => {
        this._appInsightsService.appInsights?.trackEvent({
          name: 'ServiceWorker:CheckForUpdateFailed',
          properties: { reason },
        });

        this._newRelicService.newRelic?.addPageAction('ServiceWorker:CheckForUpdateFailed', {
          reason,
        });
      });
    });
  }

  private async activateAndReload(event: VersionReadyEvent) {
    const isActivated = await this._updates.activateUpdate().catch((error) => {
      this._appInsightsService.appInsights?.trackEvent({
        name: 'ServiceWorker:UpdateActivatedError',
        properties: {
          error,
          currentHash: event.currentVersion.hash,
          latestHash: event.latestVersion.hash,
        },
      });

      this._newRelicService.newRelic?.addPageAction('ServiceWorker:UpdateActivatedError', {
        error,
        currentHash: event.currentVersion.hash,
        latestHash: event.latestVersion.hash,
      });

      return false;
    });
    this._appInsightsService.appInsights?.trackEvent({
      name: 'ServiceWorker:UpdateActivated',
      properties: {
        currentHash: isActivated ? event.latestVersion.hash : event.currentVersion.hash,
        previousHash: isActivated ? event.currentVersion.hash : (null as any),
      },
    });

    this._newRelicService.newRelic?.addPageAction('ServiceWorker:UpdateActivated', {
      currentHash: isActivated ? event.latestVersion.hash : event.currentVersion.hash,
      previousHash: isActivated ? event.currentVersion.hash : (null as any),
    });

    window.location.reload();
  }

  private listenForAvailableUpdates() {
    this._updates.versionUpdates.pipe(takeUntil(this._onDestroy$)).subscribe(async (event) => {
      if (event.type === 'VERSION_READY') {
        this._appInsightsService.appInsights?.trackEvent({
          name: 'ServiceWorker:UpdateAvailable',
          properties: {
            currentHash: event.currentVersion.hash,
            availableHash: event.latestVersion.hash,
          },
        });

        this._newRelicService.newRelic?.addPageAction('ServiceWorker:UpdateAvailable', {
          currentHash: event.currentVersion.hash,
          availableHash: event.latestVersion.hash,
        });

        const secondsTillRefresh = 60;
        const message = (seconds: number) =>
          `Click "Update" to update now. The application will update automatically in ${seconds} seconds.`;

        const alert = this._dialogService.openAlert({
          title: 'Application Update Available',
          message: message(secondsTillRefresh),
          closeButton: 'Update',
        });
        countdownInSeconds(secondsTillRefresh)
          .pipe(takeUntil(this._onDestroy$))
          // eslint-disable-next-line rxjs/no-nested-subscribe
          .subscribe({
            next: (seconds) => {
              if (alert != null && alert.componentInstance != null) {
                alert.componentInstance.message = message(seconds);
              }
            },
            error: (err) => {},
            complete: async () => {
              this._appInsightsService.appInsights?.trackEvent({
                name: 'ServiceWorker:ActivateTriggeredByTimeout',
                properties: {
                  currentHash: event.currentVersion.hash,
                  availableHash: event.latestVersion.hash,
                },
              });

              this._newRelicService.newRelic?.addPageAction(
                'ServiceWorker:ActivateTriggeredByTimeout',
                {
                  currentHash: event.currentVersion.hash,
                  availableHash: event.latestVersion.hash,
                },
              );

              await this.activateAndReload(event);
            },
          });

        await firstValueOrUndefined(alert.beforeClosed().pipe(takeUntil(this._onDestroy$)));
        this._appInsightsService.appInsights?.trackEvent({
          name: 'ServiceWorker:ActivateTriggeredByUser',
          properties: {
            currentHash: event.currentVersion.hash,
            availableHash: event.latestVersion.hash,
          },
        });

        this._newRelicService.newRelic?.addPageAction('ServiceWorker:ActivateTriggeredByUser', {
          currentHash: event.currentVersion.hash,
          availableHash: event.latestVersion.hash,
        });

        await this.activateAndReload(event);
      } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this._appInsightsService.appInsights?.trackEvent({
          name: 'ServiceWorker:UpdateInstallationFailed',
          properties: {
            versionHash: event.version.hash,
            error: event.error,
          },
        });
        this._newRelicService.newRelic?.addPageAction('ServiceWorker:UpdateInstallationFailed', {
          versionHash: event.version.hash,
          error: event.error,
        });
        await this.alertAndReloadPage();
      }
    });
  }

  private listenForUnrecoverableUpdates() {
    this._updates.unrecoverable.pipe(takeUntil(this._onDestroy$)).subscribe(async (event) => {
      this._appInsightsService.appInsights?.trackEvent({
        name: 'ServiceWorker:UnrecoverableUpdate',
        properties: { reason: event.reason },
      });
      this._newRelicService.newRelic?.addPageAction('ServiceWorker:UnrecoverableUpdate', {
        reason: event.reason,
      });
      await this.alertAndReloadPage();
    });
  }

  private async alertAndReloadPage() {
    const alert = this._dialogService.openAlert({
      title: 'Update Error',
      message: 'An error occurred while updating the application. Click OK to reload the page.',
      closeButton: 'OK',
    });
    await firstValueOrUndefined(alert.beforeClosed().pipe(takeUntil(this._onDestroy$)));
    window.location.reload();
  }

  public init() {
    if (this._updates.isEnabled) {
      this.pollForUpdates();
      this.listenForAvailableUpdates();
      this.listenForUnrecoverableUpdates();
    }
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
